import {IAccessToken} from '../api/api.types';
/**
 * Service for managing the local JSON web token (JWT) user token.
 */
export interface ITokenStorageService {
  /**
   * Retrieve the current token.
   */
  getToken():IAccessToken;
  /**
   * Changes the token and stores it in local storage.
   */
  setToken(token:IAccessToken):void;
  /**
   * Deletes the token.
   */
  clearToken():void;
  /**
   * Check if the token is still valid. It can only check public properties
   * of the token.
   *
   * Integrity of the token information against the signature can
   * only be verified on the server.
   */
  verifyToken():boolean;
}

/**
 * Default implementation of the token storage service.
 */
export default class TokenStorageService implements ITokenStorageService {
  static $inject = ['$window', '$log'];
  private static STORAGE_KEY = 'imomo.accessToken';

  /**
   * Access token parsed from the stored string.
   */
  private accessToken:IAccessToken;
  /**
   * Whether local storage is enabled in the browser.
   */
  private enabledLocalStorage:boolean;

  constructor(private $window:ng.IWindowService, private $log:ng.ILogService) {
    try {
      this.$window.localStorage.getItem(TokenStorageService.STORAGE_KEY);
      this.enabledLocalStorage = true;
    } catch (error) {
      $log.error('Local storage is not enabled.', error);
      this.enabledLocalStorage = false;
    }
    this.loadToken();
    $window.addEventListener('storage', (storageEvent:StorageEvent) => {
      this.onStorageEvent(storageEvent);
    });
  }

  getToken():IAccessToken {
    return this.accessToken;
  }

  setToken(token:IAccessToken) {
    this.accessToken = token;
    this.$window.localStorage.setItem(
        TokenStorageService.STORAGE_KEY,
        JSON.stringify(this.accessToken));
  }

  clearToken() {
    this.accessToken = null;
    this.$window.localStorage
        .removeItem(TokenStorageService.STORAGE_KEY);
  }

  verifyToken() {
    // TODO: Implement
    return this.getToken() != null;
  }

  /**
   * When the local storage changes, load the token if the value under
   * the access token storage key changed.
   */
  private onStorageEvent(storageEvent:StorageEvent) {
    if (storageEvent.key == TokenStorageService.STORAGE_KEY) {
      this.loadToken();
    }
  }

  /**
   * Load and parsed the token from local storage, if invalid then clear
   * the stored value.
   */
  private loadToken() {
    if (this.enabledLocalStorage) {
      const storedToken = this.$window.localStorage
          .getItem(TokenStorageService.STORAGE_KEY);
      try {
        this.accessToken = <IAccessToken>JSON.parse(storedToken);
      } catch (e) {
        this.$log.error('Stored text is not valid.', e);
        this.clearToken();
      }
    }
  }
}



/** WEBPACK FOOTER **
 ** ./app/auth/token-storage.service.ts
 **/